import {AxiosError} from "axios";
import {
    Bin as DeleteIcon,
    Check as TrustIcon,
    DatabaseExport as ExportIcon,
    Import as ImportKeyringIcon,
    Key as PGPIcon,
    Plus as CreateNewKeyIcon,
    Prohibition as RevokeIcon,
    Refresh as RefreshIcon,
    ShareIos as SubmitIcon,
    ShieldSearch as FindKeysIcon,
    Xmark as DistrustIcon
} from "iconoir-react";
import {useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {
    Expired,
    Match,
    MissingKeyAlias,
    PGPApi,
    PGPKeyDetails,
    PGPKeyringApi,
    PGPKeyServerClientApi,
    PGPKeyType,
    PGPSearchField,
    PGPTrustListApi,
    PGPTrustListStatus
} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {PGPCreateNewKeyDialog} from "./PGPCreateNewKeyDialog";
import {PGPKeyDetailsDialogs} from "./PGPKeyDetailsDialog";
import {PGPKeyFilter} from "./PGPKeyFilter";
import {PGPKeyRingExportDialog} from "./PGPKeyRingExportDialog";
import {PGPKeyRingImportDialog} from "./PGPKeyRingImportDialog";
import {PGPKeyRow} from "./PGPKeyRow";
import {PGPKeyServerDialog} from "./PGPKeyServerDialog";
import {PGPKeyTableHeader} from "./PGPKeyTableHeader";

/*
 * REST api for accessing the back-end
 */
const pgpKeyringApi = new PGPKeyringApi(ApiConfig.config());
const pgpTrustListApi = new PGPTrustListApi(ApiConfig.config());
const pgpKeyServerClientApi = new PGPKeyServerClientApi(ApiConfig.config());
const pgpApi = new PGPApi(ApiConfig.config());

export const PGPKeyRing = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * Represents the current location object in a React Router application.
     */
    const location = useLocation()
    /**
     * The loaded PGP keys
     */
    const [keys, setKeys] = useState<PGPKeyDetails[]>([]);
    /**
     * The total number of keys
     */
    const [keyCount, setKeyCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of keys to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected keys
     */
    const [selectedKeys, setSelectedKeys] = useState<PGPKeyDetails[]>([]);
    /**
     * The key for which the details will be shown
     */
    const [key, setKey] = useState<PGPKeyDetails>();
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm refresh keys" modal dialog will be shown
     */
    const [showRefreshKeysConfirm, setShowRefreshKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm revoke keys" modal dialog will be shown
     */
    const [showRevokeKeysConfirm, setShowRevokeKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm submit keys" modal dialog will be shown
     */
    const [showSubmitKeysConfirm, setShowSubmitKeysConfirm] = useState<boolean>(false);
    /**
     * If true, the import Keyring dialog will be shown
     */
    const [showKeyRingImportDialog, setShowKeyRingImportDialog] = useState<boolean>(false);
    /**
     * If true, the export Keyring dialog will be shown
     */
    const [showKeyRingExportDialog, setShowKeyRingExportDialog] = useState<boolean>(false);
    /**
     * If true, the Key Server dialog will be shown
     */
    const [showKeyServerDialog, setShowKeyServerDialog] = useState<boolean>(false);
    /**
     * If true, the Key details dialog will be shown
     */
    const [showKeyDetailsDialog, setShowKeyDetailsDialog] = useState<boolean>(false);
    /**
     * If true, the create new key dialog will be shown
     */
    const [showCreateNewKeyDialog, setShowCreateNewKeyDialog] = useState<boolean>(false);
    /**
     * Determines whether expired keys are shown
     */
    const [expired, setExpired] = useState<Expired>(location?.state?.expired??Expired.All);
    /**
     * Determines whether keys without private keys are shown
     */
    const [missingKeyAlias, setMissingKeyAlias] = useState<MissingKeyAlias>(location?.state?.missingKeyAlias??MissingKeyAlias.Allowed);
    /**
     * Determines whether only master keys and/or sub keys will be shown
     */
    const [keyType, setKeyType] = useState<PGPKeyType>(PGPKeyType.MasterKey);
    /**
     * The field to search on
     */
    const [searchField, setSearchField] = useState<PGPSearchField>(PGPSearchField.Email);
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * If true, the keys will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        logger.debug("Getting PGP key count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setForceReload(false);

        let loadCallback;

        if ((activeSearchValue??"").length > 0)
        {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerSearchKeysCount(`%${activeSearchValue??""}%`,
                    searchField, Match.Like, expired, missingKeyAlias, keyType);
            };
        }
        else {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerGetKeysCount(expired, missingKeyAlias, keyType);
            };
        }

        loadCallback()
            .then(response => {
                setKeyCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [expired,
        missingKeyAlias,
        keyType,
        activeSearchValue,
        searchField,
        navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        logger.debug("Getting PGP keys")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // reset state when we load new data
        setSelectedKeys([]);
        setForceReload(false);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let loadCallback;

        if ((activeSearchValue??"").length > 0)
        {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerSearchKeys(`%${activeSearchValue??""}%`,
                    searchField, Match.Like, expired, missingKeyAlias, keyType,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }
        else {
            loadCallback = async () => {
                return await pgpKeyringApi.pGPKeyringControllerGetKeys(expired, missingKeyAlias, keyType,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        loadCallback().then(response => {
            setKeys(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [expired,
        missingKeyAlias,
        keyType,
        activeSearchValue,
        searchField,
        navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    /**
     * Deletes selected keys. This will be called when the delete button of the "delete confirm" dialog is clicked.
     */
    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerDeleteKeys(selectedKeys.map(k => k.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedKeys([]);
        })
    }

    /**
     * Refresh selected keys. This will be called when the refresh button of the "download confirm" dialog is clicked.
     */
    const refreshSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyServerClientApi.pGPKeyServerClientControllerRefreshKeys(selectedKeys.map(k => k.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error refreshing PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowRefreshKeysConfirm(false);
            setSelectedKeys([]);
        })
    }

    /**
     * Revoke selected secret keys. This will be called when the revoke button of the "revoke confirm" dialog is clicked.
     */
    const revokeSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpApi.pGPControllerRevokeKeys(selectedKeys.map(k => k.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error refreshing PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowRevokeKeysConfirm(false);
            setSelectedKeys([]);
        })
    }


    /**
     * submit selected keys to a remote PGP keyserver
     */
    const submitSelectedKeys = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyServerClientApi.pGPKeyServerClientControllerSubmitKeys(selectedKeys.map(k => k.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error submitting PGP keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowSubmitKeysConfirm(false);
            setSelectedKeys([]);
        })
    }

    /**
     * Called when a key is selected (i.e., when the checkbox is checked)
     */
    const onSelectKey = (checked : boolean, key : PGPKeyDetails) =>
    {
        setSelectedKeys(checked ?
            [...selectedKeys, key] :
            selectedKeys.filter(e => e.fingerprint !== key.fingerprint)
        );
    }

    /**
     * Inverts the selection of keys by updating the list of selected keys.
     * Each previously unselected key will be selected, and each previously
     * selected key will be unselected.
     */
    const invertSelection = () => {
        setSelectedKeys([...keys].filter(cert => !selectedKeys.includes(cert)));
    }

    /**
     * Is called when the PGPKeyRingImportDialog is closed
     */
    const onKeyRingImportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyRingImportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Is called when the PGPKeyRingExportDialog is closed
     */
    const onKeyRingExportDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyRingExportDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            // clear the selected keys after export
            setSelectedKeys([]);
        }
    }

    /**
     * Is called when the PGPKeyServerDialog is closed
     */
    const onKeyServerDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyServerDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Is called when the PGPKeyDetailsDialog is closed
     */
    const onKeyDetailsDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowKeyDetailsDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Is called when the PGPCreateNewKeyDialog is closed
     */
    const onCreateNewKeyDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowCreateNewKeyDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            setForceReload(true);
        }
    }

    /**
     * Sets the trust status of selected PGP keys.
     */
    const setTrustStatusOfSelectedKeys = (trustStatus : PGPTrustListStatus) =>
    {
        pgpTrustListApi.pGPTrustListControllerSetPGPTrustListEntries(trustStatus,
                selectedKeys.map(k => k.sha256Fingerprint), true)
            .then(() =>
            {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error trusting keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
                setSelectedKeys([]);
            })
    }

    function onClickKeyId(key : PGPKeyDetails)
    {
        setKey(key);
        setShowKeyDetailsDialog(true);
    }

    return (
        <>
            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-delete-selected")}
                           confirmButtonTitle="Delete"
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-refresh-selected")}
                           confirmButtonTitle="Refresh"
                           onClickConfirmEvent={refreshSelectedKeys}
                           showConfirm={showRefreshKeysConfirm}
                           setShowConfirm={setShowRefreshKeysConfirm}
                           buttonVariant="success"
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-revoke-secret")}
                           confirmButtonTitle="Revoke"
                           onClickConfirmEvent={revokeSelectedKeys}
                           showConfirm={showRevokeKeysConfirm}
                           setShowConfirm={setShowRevokeKeysConfirm}
            />

            <ConfirmDialog title= {t("pgp-key-ring.confirm")}
                           bodyText={t("pgp-key-ring.sure-submit-keys")}
                           confirmButtonTitle="Submit"
                           onClickConfirmEvent={submitSelectedKeys}
                           showConfirm={showSubmitKeysConfirm}
                           setShowConfirm={setShowSubmitKeysConfirm}
            />

            {showKeyRingImportDialog && <PGPKeyRingImportDialog onClose={onKeyRingImportDialogClose} />}
            {showKeyRingExportDialog && <PGPKeyRingExportDialog keys={selectedKeys} onClose={onKeyRingExportDialogClose} />}
            {showKeyServerDialog && <PGPKeyServerDialog onClose={onKeyServerDialogClose} />}
            {showKeyDetailsDialog && key !== undefined && <PGPKeyDetailsDialogs pgpKey={key} onClose={onKeyDetailsDialogClose} />}
            {showCreateNewKeyDialog && <PGPCreateNewKeyDialog onClose={onCreateNewKeyDialogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("pgp.title")} icon={<PGPIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <div className="d-xl-flex">
                            <PGPKeyFilter expired={expired}
                                          setExpired={setExpired}
                                          missingKeyAlias={missingKeyAlias}
                                          setMissingKeyAlias={setMissingKeyAlias}
                                          keyType={keyType}
                                          setKeyType={setKeyType}
                                          searchField={searchField}
                                          setSearchField={setSearchField}
                                          searchValue={searchValue}
                                          setSearchValue={setSearchValue}
                                          activeSearchValue={activeSearchValue}
                                          setActiveSearchValue={setActiveSearchValue}
                            />
                        </div>

                        <div className="ms-4"/>

                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowKeyRingImportDialog(true)}>
                                <ImportKeyringIcon className="text-success"/> {t("pgp-key-ring.import-keyring")}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setShowKeyServerDialog(true)}>
                                <FindKeysIcon className="text-success"/> {t("pgp-key-ring.find-keys")}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => setShowCreateNewKeyDialog(true)}>
                                <CreateNewKeyIcon className="text-success"/> {t("pgp-key-ring.create-new-key")}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>


            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        {selectedKeys.length === 0 ?
                            <PGPKeyTableHeader>
                                {/* Add icon to first column which will invert selection when clicked */}
                                <InvertSelection invertSelectionEvent={invertSelection}/>
                            </PGPKeyTableHeader>
                            :
                            <thead className="sticky-top">
                            <tr className="text-nowrap">
                                <th colSpan={20}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedKeys([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedKeys.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger" /> {t("pgp-key-ring.delete-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setShowKeyRingExportDialog(true)}>
                                                <ExportIcon className="text-success"/> {t("pgp-key-ring.export-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setTrustStatusOfSelectedKeys(PGPTrustListStatus.Trusted)}>
                                                <TrustIcon className="text-success"/> {t("pgp-key-ring.trust-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setTrustStatusOfSelectedKeys(PGPTrustListStatus.Untrusted)}>
                                                <DistrustIcon className="text-success"/> {t("pgp-key-ring.distrust-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setShowRefreshKeysConfirm(true)}>
                                                <RefreshIcon className="text-success"/> {t("pgp-key-ring.refresh-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setShowRevokeKeysConfirm(true)}>
                                                <RevokeIcon className="text-danger" /> {t("pgp-key-ring.revoke-selected")}
                                            </Nav.Link>
                                            <Nav.Link onClick={() => setShowSubmitKeysConfirm(true)}>
                                                <SubmitIcon className="text-success" /> {t("pgp-key-ring.submit-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            </tr>
                            </thead>
                        }
                        <tbody>
                        {keys.map((key : PGPKeyDetails) => (
                            <PGPKeyRow key={key.fingerprint} pgpKey={key} onClickKey={() => onClickKeyId(key)}>
                                {/* Add selection checkbox to first column */}
                                <Form.Check
                                    type="checkbox"
                                    className="me-0"
                                    checked={selectedKeys.find((skey) =>  skey.fingerprint === key.fingerprint) !== undefined}
                                    onChange={(e) => onSelectKey(e.target.checked, key)}
                                    inline
                                />
                            </PGPKeyRow>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {keyCount} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={keyCount}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
